import React, { useState, useEffect, useMemo } from 'react';
import { getMdas, getActiveServices } from '../services/api';
import * as LucideIcons from 'lucide-react';

const IconComponent = ({ iconName, ...props }) => {
    const Icon = LucideIcons[iconName];
    const DefaultIcon = LucideIcons['Building'];
    if (!iconName || !Icon) {
        return <DefaultIcon {...props} />;
    }
    return <Icon {...props} />;
};

function ProviderListPage({ searchQuery }) {
    const [mdaList, setMdaList] = useState([]);
    const [serviceList, setServiceList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        Promise.all([
            getMdas(),
            getActiveServices({ type: 'public' })
        ]).then(([mdasResponse, servicesResponse]) => {
            setMdaList(mdasResponse.data || []);
            setServiceList(servicesResponse.data || []);
        }).catch(err => {
            setError(err.response?.data?.message || "Failed to load data.");
            setMdaList([]);
            setServiceList([]);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const serviceCounts = useMemo(() => {
        const counts = {};
        if (!Array.isArray(serviceList)) return counts;
        serviceList.forEach(service => {
            if (service.mdaId) {
                counts[service.mdaId] = (counts[service.mdaId] || 0) + 1;
            }
        });
        return counts;
    }, [serviceList]);

    const filteredMdaList = useMemo(() => {
        let mdas = mdaList.filter(mda => (serviceCounts[mda.id] || 0) > 0);

        if (searchQuery) {
            const lowercasedQuery = searchQuery.toLowerCase();
            mdas = mdas.filter(mda =>
                mda.fullName.toLowerCase().includes(lowercasedQuery) ||
                (mda.shortName && mda.shortName.toLowerCase().includes(lowercasedQuery))
            );
        }

        return mdas;
    }, [mdaList, serviceCounts, searchQuery]);

    const handleViewServicesClick = (mdaId) => {
        window.location.href = `/services?mdaId=${mdaId}`;
    };

    if (loading) return <p className="loading-message">Loading Service Providers...</p>;
    if (error) return <p className="error-message">Error: {error}</p>;

    return (
        <div className="provider-list-container">
            {filteredMdaList.length === 0 ? (
                <p>No Service Providers with active services found.</p>
            ) : (
                <ul className="provider-list">
                    {filteredMdaList.map((mda) => {
                        const count = serviceCounts[mda.id] || 0;
                        const description = `Services offered by ${mda.shortName || mda.fullName}.`;

                        return (
                            <li key={mda.id} className="provider-list-item">
                                <div className="provider-icon">
                                    <IconComponent iconName={mda.mdaIcon} size={32} />
                                </div>
                                <div className="provider-details">
                                    <h3 className="provider-name">{mda.fullName}</h3>
                                    <p className="provider-description">{description}</p>
                                </div>
                                <div className="provider-actions">
                                    <span className="provider-service-count-badge">
                                        {count} Services
                                    </span>
                                    <button className="provider-view-button bg-green-600 hover:bg-green-700" onClick={() => handleViewServicesClick(mda.id)}>View Services</button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default ProviderListPage;
